<template>
  <UiPopover ref="uiPopover" animate @toggle="trackToggle">
    <template #button="{ toggle, isOpen }">
      <div data-testid="popoverTrigger">
        <slot name="trigger" :is-open="isOpen" :toggle-popover="toggle">
          <UiButton
            :size="buttonSize"
            appearance="outline-emphasis"
            data-testid="popoverTriggerButton"
            :class="{ 'flex gap-x-2': isDropdown }"
            @click="
              toggle();
              handleTriggerClicked();
            ">
            {{ buttonLabel }}
            <UiIcon v-if="isDropdown" name="chevron-down" />
          </UiButton>
        </slot>
      </div>
    </template>

    <template #default="{ close: closeUiPopover }">
      <div
        data-testid="popoverContent"
        :class="
          cn(
            'w-full rounded-lg border border-neutral-faint bg-neutral shadow-md',
            popoverClass,
          )
        ">
        <div
          v-if="popoverLabel || hasCloseIcon"
          class="flex flex-nowrap items-center justify-between gap-x-4">
          <div
            v-if="popoverLabel"
            class="text-xs font-semibold tracking-wide text-neutral-subtle uppercase">
            {{ popoverLabel }}
          </div>
          <div v-if="hasCloseIcon" class="col-start-2 flex justify-end">
            <UiButton
              :title="t('basic.close')"
              appearance="ghost"
              size="sm"
              @click="() => handleClose(closeUiPopover)">
              <UiIcon name="close" />
            </UiButton>
          </div>
        </div>
        <slot
          name="content"
          :close-popover="() => handleClose(closeUiPopover)" />
      </div>
    </template>
  </UiPopover>
</template>

<script lang="ts" setup>
import UiButton from "@dpa-id-components/dpa-shared-components/src/components/UiButton/UiButton.vue";
import UiIcon from "@dpa-id-components/dpa-shared-components/src/components/UiIcon/UiIcon.vue";
import UiPopover from "@dpa-id-components/dpa-shared-components/src/components/UiPopover/UiPopover.vue";
import { cn } from "@dpa-id-components/dpa-shared-components/src/utils/cn.ts";
import type { ClassValue } from "clsx";
import { useTemplateRef } from "vue";
import { useI18n } from "vue-i18n";

const {
  buttonSize = "xs",
  hasCloseIcon = true,
  buttonLabel = undefined,
  popoverLabel = undefined,
  popoverClass = undefined,
  isDropdown = true,
} = defineProps<{
  buttonSize?: "xs" | "sm";
  buttonLabel?: string;
  popoverLabel?: string;
  hasCloseIcon?: boolean;
  popoverClass?: ClassValue | ClassValue[];
  isDropdown?: boolean;
}>();

const uiPopoverRef =
  useTemplateRef<InstanceType<typeof UiPopover>>("uiPopover");

const { t } = useI18n();

function trackToggle(isOpen: boolean) {
  if (isOpen) {
    emit("popoverOpened");
  } else {
    emit("popoverClosed");
  }
}

function handleTriggerClicked() {
  emit("triggerClicked");
}

function handleClose(close: () => void) {
  close();
}

function open() {
  uiPopoverRef.value?.open();
}

function close() {
  uiPopoverRef.value?.close();
}

defineExpose({ open, close });

const emit = defineEmits<{
  triggerClicked: [];
  popoverOpened: [];
  popoverClosed: [];
}>();
</script>
